import React from 'react';
import { motion } from 'framer-motion';
import { useStory, STEPS } from '../../context/StoryContext';
import { api } from '../../services/api';
import { BookOpen } from 'lucide-react';
import BackArrow from '../ui/BackArrow';

const TURRIS_LOGO = '/logo-turris.svg';
const COVER_NOI_TEMPLATE = '/story/portada-noi.png';
const COVER_NOIA_TEMPLATE = '/story/portada-noia.png';

export default function CoverScreen() {
  const { goToStep, goBack, protagonistName, protagonistType } = useStory();

  const handleContinue = () => {
    api.trackEvent('cover_viewed', { 
      protagonist_name: protagonistName,
      protagonist_type: protagonistType
    });
    goToStep(STEPS.PREVIEW);
  };

  // Helper function to check if name starts with vowel (Catalan elision rules)
  const startsWithVowel = (name) => {
    if (!name) return false;
    const firstLetter = name.charAt(0).toLowerCase();
    return 'aeiouàèéíïòóúüh'.includes(firstLetter);
  };

  // Select template and article based on protagonist type and name's first letter
  const coverTemplate = protagonistType === 'noi' ? COVER_NOI_TEMPLATE : COVER_NOIA_TEMPLATE;
  
  // Apply Catalan elision rules: "del Marc" but "de l'Oriol", "de la Maria" but "de l'Anna"
  const getArticle = () => {
    if (startsWithVowel(protagonistName)) {
      return "de l'"; // Elision for vowel-starting names (both masculine and feminine)
    }
    return protagonistType === 'noi' ? 'del ' : 'de la ';
  };
  
  const article = getArticle();

  return (
    <div 
      className="min-h-screen h-screen flex flex-col overflow-hidden"
      style={{ backgroundColor: '#FDFBF7' }}
    >
      {/* Header */}
      <header className="p-3 lg:p-4 flex items-center justify-between flex-shrink-0">
        <BackArrow onClick={goBack} />
        <img src={TURRIS_LOGO} alt="Turris" className="h-8 lg:h-10" />
        <div className="w-10" />
      </header>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 py-1 lg:py-2 flex-shrink-0">
        <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full" style={{ backgroundColor: '#D32F2F' }} />
        <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full" style={{ backgroundColor: '#E0E0E0' }} />
        <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full" style={{ backgroundColor: '#E0E0E0' }} />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-2 lg:py-4 overflow-hidden">
        <motion.h1
          className="font-heading text-lg lg:text-xl font-bold text-center mb-2 lg:mb-3 flex-shrink-0"
          style={{ color: '#1a365d' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          La teva portada personalitzada!
        </motion.h1>

        {/* Cover with personalized name */}
        <motion.div
          className="relative w-full max-w-[240px] lg:max-w-[280px] xl:max-w-[320px] flex-shrink-0"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Cover template image */}
          <img
            src={coverTemplate}
            alt="Portada del conte"
            className="w-full rounded-lg shadow-2xl"
            style={{
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)'
            }}
          />
          
          {/* Personalized name overlay - centered below title */}
          <div 
            className="absolute w-full flex flex-col items-center"
            style={{
              top: '26%',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '0 10px',
            }}
          >
            {/* Combined article + name on same line, properly sized */}
            <div 
              className="text-center whitespace-nowrap"
              style={{
                fontFamily: "'Fredoka One', 'Comic Sans MS', cursive",
                fontSize: 'clamp(1.1rem, 5vw, 2rem)',
                color: '#FFFFFF',
                textShadow: `
                  -2px -2px 0 #1a365d,
                  2px -2px 0 #1a365d,
                  -2px 2px 0 #1a365d,
                  2px 2px 0 #1a365d,
                  -3px 0 0 #1a365d,
                  3px 0 0 #1a365d,
                  0 -3px 0 #1a365d,
                  0 3px 0 #1a365d
                `,
                letterSpacing: '1px'
              }}
            >
              <span style={{ color: '#FFD700' }}>{article}</span>
              {protagonistName}
            </div>
          </div>
          
          {/* Footer text */}
          <div 
            className="absolute w-full flex justify-center"
            style={{
              bottom: '3%',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '0 10px',
            }}
          >
            <span
              className="text-center"
              style={{
                fontFamily: "'Georgia', serif",
                fontSize: 'clamp(0.5rem, 2.5vw, 0.85rem)',
                color: '#FFFFFF',
                fontStyle: 'italic',
                letterSpacing: '0.5px',
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Una història per compartir al voltant del pa
            </span>
          </div>
        </motion.div>

        {/* Continue button */}
        <motion.button
          data-testid="preview-book-button"
          onClick={handleContinue}
          className="mt-4 lg:mt-5 px-6 lg:px-8 py-3 lg:py-4 rounded-full text-base lg:text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 flex items-center gap-2 flex-shrink-0"
          style={{ 
            backgroundColor: '#D32F2F',
            boxShadow: '0 8px 24px rgba(211, 47, 47, 0.25)'
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <BookOpen className="w-5 h-5" />
          Fullejar el conte
        </motion.button>

        <motion.p
          className="mt-2 text-xs lg:text-sm text-center flex-shrink-0"
          style={{ color: '#666' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          Fes clic per veure les pàgines interiors
        </motion.p>
      </main>
    </div>
  );
}
